import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { HeroSlide } from "@/types/content";
import Container from "@/components/base/Container";

interface HeroBannerProps {
  slides: HeroSlide[];
}

export default function HeroBanner({ slides }: HeroBannerProps) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const total = slides.length;

  const goTo = useCallback(
    (next: number) => {
      setIndex(((next % total) + total) % total);
    },
    [total]
  );

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [total]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      goTo(index + (delta < 0 ? 1 : -1));
    }
    touchStartX.current = null;
  };

  const active = slides[index];

  return (
    <section
      className="relative w-full h-[440px] md:h-[560px] overflow-hidden bg-primary-950"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
          aria-hidden={i !== index}
        >
          <img
            src={slide.image}
            alt={`夜勤病栋专题焦点图：${slide.title}`}
            title={`${slide.title} - 夜勤病栋影视专题`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/45 to-primary-950/25"></div>
      <div className="absolute inset-0 bg-gradient-to-r from-primary-950/70 via-transparent to-transparent"></div>

      <Container className="relative h-full flex flex-col justify-end pb-14 md:pb-16">
        <div className="max-w-2xl">
          <div className="flex items-center gap-2 mb-4">
            <span className="px-2.5 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-medium">
              {active.kicker}
            </span>
            <span className="px-2.5 py-1 rounded-md bg-background-50/15 text-background-50 text-xs backdrop-blur-sm border border-background-50/20">
              {active.tag}
            </span>
          </div>
          <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50 leading-snug text-balance">
            {active.title}
          </h1>
          <p className="mt-3 md:mt-4 text-sm md:text-base text-background-200 leading-relaxed max-w-xl">
            {active.description}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/topic`}
              className="px-5 h-11 flex items-center gap-2 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-book-open-line"></i>
              查看专题
            </Link>
            <Link
              to={`/detail/${active.filmId}`}
              className="px-5 h-11 flex items-center gap-2 rounded-md bg-background-50/12 text-background-50 text-sm border border-background-50/30 hover:bg-background-50/20 transition-colors whitespace-nowrap cursor-pointer backdrop-blur-sm"
            >
              <i className="ri-information-line"></i>
              作品详情
            </Link>
          </div>
        </div>
      </Container>

      <button
        type="button"
        onClick={() => goTo(index - 1)}
        aria-label="上一张"
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-background-50/12 text-background-50 border border-background-50/25 hover:bg-background-50/25 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        type="button"
        onClick={() => goTo(index + 1)}
        aria-label="下一张"
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-background-50/12 text-background-50 border border-background-50/25 hover:bg-background-50/25 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {slides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            aria-label={`切换到第${i + 1}张`}
            onClick={() => goTo(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-7 bg-accent-500" : "w-3 bg-background-50/50"
            }`}
          />
        ))}
      </div>
    </section>
  );
}